import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form } from "react-bootstrap";

const POSTS_PER_PAGE = 9;

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [category, setCategory] = useState("Custom Homes");
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  /* ---------------- HASHNODE FETCH (ALL POSTS) ---------------- */
 const fetchPosts = async (host) => {
  let allPosts = [];
  let hasNextPage = true;
  let afterCursor = null;

  try {
    while (hasNextPage) {
      const response = await fetch("https://gql.hashnode.com", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            query GetPosts($host: String!, $after: String) {
              publication(host: $host) {
                posts(first: 20, after: $after) {
                  edges {
                    node {
                      title
                      brief
                      slug
                      publishedAt
                      url
                      coverImage {
                        url
                      }
                      content {
                        markdown
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
            }
          `,
          variables: {
            host,
            after: afterCursor,
          },
        }),
      });

      const result = await response.json();
      const postsData = result?.data?.publication?.posts;
      if (!postsData) break;

      const pagePosts = postsData.edges.map((edge) => ({
        ...edge.node,
        source: "hashnode",
        sourceHost: host,
      }));

      allPosts.push(...pagePosts);

      hasNextPage = postsData.pageInfo.hasNextPage;
      afterCursor = postsData.pageInfo.endCursor;
    }

    return allPosts;
  } catch (error) {
    console.error(`Failed to fetch from ${host}`, error);
    return [];
  }
};

  /* ---------------- LOAD ALL BLOGS ---------------- */
 useEffect(() => {
  const loadAllPosts = async () => {
    const results = await Promise.allSettled([
      fetchPosts("luxurycustomhomes1.hashnode.dev"),
      fetchPosts("customhomes5.hashnode.dev"),
      fetchPosts("luxurycustomhomes.hashnode.dev"),
      fetchPosts("designtrends.hashnode.dev"),

      // DreamBuildDigest (covers ALL links)
      fetchPosts("dreambuilddigest.hashnode.dev"),

      fetchPosts("luxuryhomes3.hashnode.dev"),
      fetchPosts("stepstobuyingahouse.hashnode.dev"),
      fetchPosts("guidetochoosingtheperfectneighborhood.hashnode.dev"),
      fetchPosts("besttimetoinvestinrealestate.hashnode.dev"),
      fetchPosts("chooserighitbuilder.hashnode.dev"),
      fetchPosts("rentingvsbuyingwhichisrightforyou.hashnode.dev"),
      fetchPosts("bestpriceforyourdreamhome.hashnode.dev"),
      fetchPosts("whattoexpectduringahomeinspection.hashnode.dev"),
      fetchPosts("adviceforfirst-timehomebuyers.hashnode.dev"),
      fetchPosts("luxuryhomes4.hashnode.dev"),
      fetchPosts("luxuryhomes5.hashnode.dev"),
      fetchPosts("smarthometechnology.hashnode.dev"),
      fetchPosts("ecofriendlyhomeupgrades.hashnode.dev"),
      fetchPosts("buyinghome.hashnode.dev"),
      fetchPosts("markettrends2025.hashnode.dev"),

      fetchPosts("customhomes1.hashnode.dev"),
      fetchPosts("customhomes2.hashnode.dev"),
      fetchPosts("customhomes3.hashnode.dev"),
      fetchPosts("customhomes9.hashnode.dev"),

      fetchPosts("customehomes1.hashnode.dev"),
      fetchPosts("customehomes3.hashnode.dev"),
      fetchPosts("customehomes4.hashnode.dev"),

      fetchPosts("productionhomes.hashnode.dev"),
      fetchPosts("productionhomes1.hashnode.dev"),
      fetchPosts("productionhome2.hashnode.dev"),
      fetchPosts("productionhomes3.hashnode.dev"),
      fetchPosts("productionhomes4.hashnode.dev"),
      fetchPosts("productionhomes5.hashnode.dev"),
      fetchPosts("productionhomes6.hashnode.dev"),
      fetchPosts("productionhomes7.hashnode.dev"),
      fetchPosts("productionhomes8.hashnode.dev"),
      fetchPosts("productionhomes9.hashnode.dev"),
      fetchPosts("productionhomes10.hashnode.dev"),
      fetchPosts("productionhomes11.hashnode.dev"),

      fetchPosts("realestatemarketpredictionsfor2025.hashnode.dev"),
      fetchPosts("seasonaltips.hashnode.dev"),
      fetchPosts("tips1.hashnode.dev"),
      fetchPosts("investment1.hashnode.dev"),
      fetchPosts("invesment2.hashnode.dev"),
      fetchPosts("investment2.hashnode.dev"),
      fetchPosts("markettrends2.hashnode.dev"),
      fetchPosts("selling.hashnode.dev"),
      fetchPosts("lifestyles.hashnode.dev"),
      fetchPosts("saller1.hashnode.dev"),

      // Tech blog
      fetchPosts("technology3.hashnode.dev"),
    ]);

    const combined = results
      .filter((r) => r.status === "fulfilled")
      .flatMap((r) => r.value || []);

    combined.sort(
      (a, b) =>
        new Date(b.publishedAt).getTime() -
        new Date(a.publishedAt).getTime()
    );

    setPosts(combined);
    setFilteredPosts(combined);
  };

  loadAllPosts();
}, []);


  /* ---------------- CATEGORY FILTER ---------------- */
  useEffect(() => {
    setFilteredPosts(posts);
    setCurrentPage(1);
  }, [category, posts]);

  /* ---------------- PAGINATION ---------------- */
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);

  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  /* ---------------- HELPERS ---------------- */
  const getReadingTime = (markdown) => {
    const wordsPerMinute = 200;
    const words = markdown?.split(/\s+/).length || 0;
    return Math.ceil(words / wordsPerMinute);
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <Container className="py-5">
        <h2 className="mb-4">Latest Blogs</h2>

        <Form.Group className="mb-4">
          <Form.Label>Select Category</Form.Label>
          <Form.Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="Custom Homes">Custom Homes</option>
          </Form.Select>
        </Form.Group>

        <Row xs={1} sm={2} md={3} className="g-4">
          {paginatedPosts.map((post, index) => (
            <Col key={index}>
              <Card
                className="h-100 shadow-sm"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`/blogs/${post.sourceHost}/${post.slug}`)
                }
              >
                {post.coverImage?.url && (
                  <Card.Img
                    variant="top"
                    src={post.coverImage.url}
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                )}
                <Card.Body>
                  <Card.Title>{post.title}</Card.Title>
                  <Card.Subtitle className="text-muted">
                    {new Date(post.publishedAt).toDateString()} ·{" "}
                    {getReadingTime(post.content?.markdown)} min read
                  </Card.Subtitle>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-5 gap-3">
            <button
              className="btn btn-outline-dark"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </button>

            <span className="align-self-center">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="btn btn-outline-dark"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </Container>
    </>
  );
};

export default Blogs;
